import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, ChevronLeft, ChevronRight, Sparkles, HeartHandshake } from "lucide-react";
import WavyDivider from "@/components/site/WavyDivider";
import { DecorationField, Star, Balloon, ToyBlock } from "@/components/site/Decorations";
import Reveal from "@/components/site/Reveal";
import SEO from "@/components/site/SEO";
import { IMG } from "@/lib/siteData";

const CAROUSEL_ALTS = [
  "Child playing dress-up as a superhero",
  "Young child exploring music with an instrument",
  "Young girl exploring a robotic arm science kit",
];

function Carousel({ images }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % images.length), 4500);
    return () => clearInterval(id);
  }, [images.length]);
  return (
    <div className="relative aspect-[4/5] w-full rounded-[2.5rem] overflow-hidden shadow-xl border-4 border-white" data-testid="home-carousel">
      {images.map((src, idx) => (
        <img
          key={src}
          src={src}
          alt={CAROUSEL_ALTS[idx % CAROUSEL_ALTS.length]}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            i === idx ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      <div className="absolute inset-x-0 bottom-4 flex justify-center gap-2">
        {images.map((_, idx) => (
          <button
            key={idx}
            data-testid={`carousel-dot-${idx}`}
            onClick={() => setI(idx)}
            className={`h-2.5 rounded-full transition-all ${
              i === idx ? "w-7 bg-white" : "w-2.5 bg-white/60"
            }`}
            aria-label={`Slide ${idx + 1}`}
          />
        ))}
      </div>
      <button
        onClick={() => setI((p) => (p - 1 + images.length) % images.length)}
        className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center"
        aria-label="Previous"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={() => setI((p) => (p + 1) % images.length)}
        className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center"
        aria-label="Next"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
}

export default function HomePage() {
  return (
    <div className="overflow-hidden" data-testid="home-page">
      <SEO />
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center pt-24 pb-24 md:pb-32">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <img
            src={IMG.homeHero}
            alt="Young girl exploring a robotic arm toy in class"
            data-testid="home-hero-image"
            className="w-full h-full object-cover"
            style={{
              transform: "scale(1.3) translate(-9%, 9%)",
              transformOrigin: "center center",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-purple-deep/55 via-brand-purple/25 to-transparent" />
        </div>
        <DecorationField variant="hero" />

        <div className="relative max-w-7xl mx-auto w-full px-6 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur text-white text-[13px] font-semibold tracking-wide">
              <Sparkles className="h-4 w-4 text-brand-yellow" />
              Brooklyn, NY · Ages 12 months–5
            </span>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tight text-balance leading-[1.05]">
              Welcome to <span className="text-brand-yellow">Innovators</span> Prep
            </h1>
            <div
              data-testid="home-hero-tagline"
              className="mt-5"
            >
              <span className="inline-block font-display text-sm sm:text-base font-bold tracking-[0.28em] uppercase text-brand-yellow px-4 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25">
                Play. Learn. Grow.
              </span>
            </div>
            <p className="mt-5 max-w-xl text-white/90 text-lg leading-relaxed">
              A nurturing early childhood space where curiosity is celebrated, every child is truly
              seen, and learning blooms through play.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                data-testid="hero-location-btn"
                className="bg-brand-green hover:bg-brand-green-deep text-white rounded-full px-7 py-6 text-base font-semibold shadow-lg hover:-translate-y-0.5 transition-transform"
              >
                <Link to="/contact"><MapPin className="h-5 w-5 mr-1" /> Visit Us</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="rounded-full px-7 py-6 text-base font-semibold bg-white/90 hover:bg-white text-brand-purple-deep border-white"
              >
                <Link to="/about">Discover More <ArrowRight className="h-5 w-5 ml-1" /></Link>
              </Button>
            </div>
          </Reveal>

          {/* Overlay circle */}
          <Reveal delay={0.2} className="hidden lg:block">
            <div className="relative mx-auto w-[460px] h-[460px]">
              <div className="absolute inset-0 rounded-full bg-brand-yellow/20 blur-3xl" />
              <div className="absolute inset-6 rounded-full bg-white/95 backdrop-blur-md shadow-2xl flex flex-col items-center justify-center p-10 text-center">
                <Star color="#FBBF24" size={36} />
                <h2 className="mt-4 font-display text-3xl font-bold text-gray-900 leading-tight">
                  Welcome to<br />Innovators Prep
                </h2>
                <p className="mt-3 text-sm text-gray-600 max-w-[280px]">
                  A safe, joyful, culturally responsive home for early learners.
                </p>
                <Button
                  asChild
                  data-testid="hero-overlay-location"
                  className="mt-5 bg-brand-green hover:bg-brand-green-deep text-white rounded-full font-semibold"
                >
                  <Link to="/contact"><MapPin className="h-4 w-4 mr-1" /> Location</Link>
                </Button>
                <div className="absolute -top-4 -right-2"><Balloon color="#9333EA" size={42} /></div>
                <div className="absolute -bottom-2 -left-3"><ToyBlock color="#16A34A" size={40} /></div>
              </div>
            </div>
          </Reveal>
        </div>

        <WavyDivider fill="#FFFCF9" />
      </section>

      {/* SECTION 2 — Special Education 4410 */}
      <section className="relative py-20 md:py-28">
        <DecorationField variant="mixed" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-6 bg-brand-green-soft rounded-[3rem] -rotate-2" />
              <img
                src={IMG.specialEdSection}
                alt="Children playing together during kindergarten playtime"
                className="relative rounded-[2.5rem] w-full aspect-[5/4] object-cover shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-lg px-5 py-3 flex items-center gap-3">
                <span className="h-10 w-10 rounded-xl bg-brand-purple-soft flex items-center justify-center">
                  <HeartHandshake className="h-5 w-5 text-brand-purple-deep" />
                </span>
                <div>
                  <div className="font-display text-lg font-bold text-gray-900 leading-none">NY State</div>
                  <div className="text-xs text-gray-500">4410 Approved</div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <span className="inline-block px-3 py-1 rounded-full bg-brand-purple-soft text-brand-purple-deep text-xs font-bold tracking-widest uppercase">
              Preschool Special Education
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Innovators Prep Preschool Special Education <span className="text-brand-green">(4410)</span>
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed">
              Innovators Prep is a New York State-approved 4410 Preschool Special Education Program
              serving children ages 3–5 through individualized, high-quality education and therapy. We
              offer SCIS (integrated), 12:1:2, and 8:1:2 classroom models.
            </p>
            <Button
              asChild
              data-testid="home-special-ed-learn-more"
              className="mt-7 bg-brand-green hover:bg-brand-green-deep text-white rounded-full px-7 py-6 font-semibold shadow-md hover:-translate-y-0.5 transition-transform"
            >
              <Link to="/programs/special-education">Learn More <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* SECTION 3 — Focused Nurturing + Carousel */}
      <section className="relative py-20 md:py-28 bg-brand-purple-soft/40">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="inline-block px-3 py-1 rounded-full bg-brand-green-soft text-brand-green-deep text-xs font-bold tracking-widest uppercase">
              Our Approach
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Focused Nurturing For <span className="text-brand-purple">Your Children</span>
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed">
              Innovators Prep is an early childhood centre dedicated to fostering creativity, innovation,
              and higher-order thinking. We believe in building trusting relationships with caregivers
              and truly seeing every child we work with.
            </p>
            <Button
              asChild
              data-testid="home-nurturing-learn-more"
              variant="outline"
              className="mt-7 rounded-full px-7 py-6 font-semibold border-2 border-brand-purple text-brand-purple-deep hover:bg-brand-purple-soft"
            >
              <Link to="/philosophy">Learn More <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          </Reveal>
          <Reveal delay={0.15}>
            <Carousel images={IMG.carousel} />
          </Reveal>
        </div>
      </section>

      {/* SECTION 4 — Programs cards */}
      <section id="programs" className="relative py-20 md:py-28 scroll-mt-24">
        <DecorationField variant="mixed" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full bg-brand-yellow/20 text-amber-700 text-xs font-bold tracking-widest uppercase">
              Our Programs
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Growing Curious Minds, <span className="text-brand-green">Every Step</span>
            </h2>
            <p className="mt-4 text-gray-600 text-lg">
              Choose the pathway that fits your child's unique journey.
            </p>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Preschool Program",
                ages: "Ages 2–5",
                img: IMG.programCardPreschool,
                desc: "A nurturing, play-based classroom where children explore their interests, build social skills and lay the groundwork for lifelong learning.",
                to: "/programs/preschool",
                color: "from-brand-green to-brand-green-deep",
                pill: "bg-brand-green-soft text-brand-green-deep",
                tid: "program-card-preschool",
              },
              {
                title: "Preschool Special Education Program",
                ages: "Special Classes — 4410",
                img: IMG.programCardSpecialEd,
                desc: "NYS-approved 4410 program with SCIS, 12:1:2 and 8:1:2 models, plus integrated therapies and family support.",
                to: "/programs/special-education",
                color: "from-brand-purple to-brand-purple-deep",
                pill: "bg-brand-purple-soft text-brand-purple-deep",
                tid: "program-card-special-ed",
              },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <div
                  data-testid={c.tid}
                  className="group relative bg-white rounded-[2rem] shadow-sm hover:shadow-2xl border border-gray-100 overflow-hidden transition-all hover:-translate-y-1 duration-500"
                >
                  <div className="aspect-[5/3] overflow-hidden">
                    <img
                      src={c.img}
                      alt={c.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-7 md:p-9">
                    <span className={`inline-block text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full ${c.pill}`}>
                      {c.ages}
                    </span>
                    <h3 className="mt-4 font-display text-2xl md:text-3xl font-bold text-gray-900">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-gray-600 leading-relaxed">{c.desc}</p>
                    <Button
                      asChild
                      className={`mt-6 rounded-full font-semibold text-white bg-gradient-to-r ${c.color} hover:opacity-95 px-6`}
                    >
                      <Link to={c.to}>Learn More <ArrowRight className="h-4 w-4 ml-1" /></Link>
                    </Button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
